import React, { useEffect, useState } from 'react'
import { apiFetch, apiFetchBlob, downloadBlob } from '../../api.js'
import PdfCalibrationModal from './PdfCalibrationModal.jsx'

export default function SettingsInvoice({ setFlash }) {
  // loading / saving
  const [tplLoading, setTplLoading] = useState(true)
  const [tplSaving, setTplSaving] = useState(false)

  // champs config facture
  const [companyName, setCompanyName] = useState('')
  const [companyAddress, setCompanyAddress] = useState('')
  const [companyContacts, setCompanyContacts] = useState('')
  const [companyLegal, setCompanyLegal] = useState('')
  const [footerText, setFooterText] = useState('')
  const [accentColor, setAccentColor] = useState('#000000')
  const [showLogo, setShowLogo] = useState(true)
  const [pageFormat, setPageFormat] = useState('A4')
  const [logoPath, setLogoPath] = useState(null)
  const [invoicePrefix, setInvoicePrefix] = useState('')
  const [invoiceNumberFormat, setInvoiceNumberFormat] = useState('')
  const [showTva, setShowTva] = useState(true)
  const [showRemise, setShowRemise] = useState(true)
  const [showPayments, setShowPayments] = useState(true)
  const [showInstallments, setShowInstallments] = useState(true)
  const [paymentTerms, setPaymentTerms] = useState('')
  const [ibanLabel, setIbanLabel] = useState('')
  const [ibanValue, setIbanValue] = useState('')

  // champs spécifiques paie (migration 087)
  const [siret, setSiret] = useState('')
  const [apeCode, setApeCode] = useState('')
  const [conventionCollective, setConventionCollective] = useState('')
  const [payrollPaymentMethod, setPayrollPaymentMethod] = useState('Virement')

  // modèle personnalisé
  const [useCustomTemplate, setUseCustomTemplate] = useState(false)
  const [customTemplateType, setCustomTemplateType] = useState(null)

  // mapping JSON (textarea)
  const [customFieldMapText, setCustomFieldMapText] = useState('{}')

  // positions PDF JSON (textarea)
  const [pdfFieldPositionsText, setPdfFieldPositionsText] = useState('{}')

  // modal calibration
  const [calibrationModalOpen, setCalibrationModalOpen] = useState(false)

  // preview
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewBlob, setPreviewBlob] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(null)
  const [previewOrderId, setPreviewOrderId] = useState('')

  // -------- loadTemplate --------
  async function loadTemplate() {
    try {
      setTplLoading(true)
      const data = await apiFetch('/api/settings/invoice-format')
      if (data) {
        setCompanyName(data.company_name ?? '')
        setCompanyAddress(data.company_address ?? '')
        setCompanyContacts(data.company_contacts ?? '')
        setCompanyLegal(data.company_legal ?? '')
        setFooterText(data.footer_text ?? data.footer_note ?? '')
        setAccentColor(data.accent_color ?? '#000000')
        setShowLogo(data.show_logo !== false)
        setPageFormat(data.page_format ?? 'A4')
        setLogoPath(data.logo_path ?? null)
        setInvoicePrefix(data.invoice_prefix ?? '')
        setInvoiceNumberFormat(data.invoice_number_format ?? '')
        setShowTva(data.show_tva !== false)
        setShowRemise(data.show_remise !== false)
        setShowPayments(data.show_payments !== false)
        setShowInstallments(data.show_installments !== false)
        setPaymentTerms(data.payment_terms ?? '')
        setIbanLabel(data.iban_label ?? '')
        setIbanValue(data.iban_value ?? '')
        setSiret(data.siret ?? '')
        setApeCode(data.ape_code ?? '')
        setConventionCollective(data.convention_collective ?? '')
        setPayrollPaymentMethod(data.payroll_payment_method ?? 'Virement')

        // custom template info
        setUseCustomTemplate(data.use_custom_template ?? false)
        setCustomTemplateType(data.custom_template_type ?? null)

        // mapping champs
        if (data.custom_field_map && typeof data.custom_field_map === 'object') {
          try {
            setCustomFieldMapText(JSON.stringify(data.custom_field_map, null, 2))
          } catch {
            setCustomFieldMapText('{}')
          }
        } else {
          setCustomFieldMapText('{}')
        }

        // positions pdf
        if (data.pdf_field_positions && typeof data.pdf_field_positions === 'object') {
          try {
            setPdfFieldPositionsText(
              JSON.stringify(data.pdf_field_positions, null, 2)
            )
          } catch {
            setPdfFieldPositionsText('{}')
          }
        } else {
          setPdfFieldPositionsText('{}')
        }
      }
    } catch (err) {
      console.error('Erreur chargement template facture:', err)
      setFlash('❌ Erreur chargement facture: ' + err.message)
    } finally {
      setTplLoading(false)
    }
  }

  useEffect(() => {
    loadTemplate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  // -------- handleSaveTemplate --------
  async function handleSaveTemplate(e) {
    e.preventDefault()
    setTplSaving(true)
    setFlash(null)

    // parse custom_field_map
    let parsedMap = {}
    try {
      if (customFieldMapText.trim()) {
        parsedMap = JSON.parse(customFieldMapText)
      }
    } catch {
      setTplSaving(false)
      setFlash('❌ custom_field_map invalide (JSON incorrect)')
      return
    }

    // parse pdf_field_positions
    let parsedPdfPositions = {}
    try {
      if (pdfFieldPositionsText.trim()) {
        parsedPdfPositions = JSON.parse(pdfFieldPositionsText)
      }
    } catch {
      setTplSaving(false)
      setFlash('❌ pdf_field_positions invalide (JSON incorrect)')
      return
    }

    try {
      const body = {
        company_name: companyName,
        company_address: companyAddress,
        company_contacts: companyContacts,
        company_legal: companyLegal,
        footer_note: footerText,
        footer_text: footerText,
        accent_color: accentColor,
        show_logo: showLogo,
        page_format: pageFormat,
        invoice_prefix: invoicePrefix,
        invoice_number_format: invoiceNumberFormat,
        show_tva: showTva,
        show_remise: showRemise,
        show_payments: showPayments,
        show_installments: showInstallments,
        payment_terms: paymentTerms,
        iban_label: ibanLabel,
        iban_value: ibanValue,
        siret,
        ape_code: apeCode,
        convention_collective: conventionCollective,
        payroll_payment_method: payrollPaymentMethod,
        custom_field_map: parsedMap,
        pdf_field_positions: parsedPdfPositions,
      }

      const updated = await apiFetch('/api/settings/invoice-format', {
        method: 'PUT',
        body
      })

      setFlash('✅ Modèle de facture enregistré')
      // Auto-génère un aperçu PDF après sauvegarde
      handlePreviewPdf()

      if (updated) {
        setCompanyName(updated.company_name ?? '')
        setCompanyAddress(updated.company_address ?? '')
        setCompanyContacts(updated.company_contacts ?? '')
        setCompanyLegal(updated.company_legal ?? '')
        setFooterText(updated.footer_text ?? updated.footer_note ?? '')
        setAccentColor(updated.accent_color ?? '#000000')
        setShowLogo(updated.show_logo !== false)
        setPageFormat(updated.page_format ?? 'A4')
        setLogoPath(updated.logo_path ?? null)
        setInvoicePrefix(updated.invoice_prefix ?? '')
        setInvoiceNumberFormat(updated.invoice_number_format ?? '')
        setShowTva(updated.show_tva !== false)
        setShowRemise(updated.show_remise !== false)
        setShowPayments(updated.show_payments !== false)
        setShowInstallments(updated.show_installments !== false)
        setPaymentTerms(updated.payment_terms ?? '')
        setIbanLabel(updated.iban_label ?? '')
        setIbanValue(updated.iban_value ?? '')
        setSiret(updated.siret ?? '')
        setApeCode(updated.ape_code ?? '')
        setConventionCollective(updated.convention_collective ?? '')
        setPayrollPaymentMethod(updated.payroll_payment_method ?? 'Virement')

        if (updated.custom_field_map && typeof updated.custom_field_map === 'object') {
          try {
            setCustomFieldMapText(
              JSON.stringify(updated.custom_field_map, null, 2)
            )
          } catch { /* ignore */ }
        }

        if (updated.pdf_field_positions && typeof updated.pdf_field_positions === 'object') {
          try {
            setPdfFieldPositionsText(
              JSON.stringify(updated.pdf_field_positions, null, 2)
            )
          } catch { /* ignore */ }
        }
      }
    } catch (err) {
      console.error('Erreur sauvegarde template:', err)
      setFlash('❌ Erreur sauvegarde facture: ' + err.message)
    } finally {
      setTplSaving(false)
    }
  }

  async function handlePreviewPdf() {
    try {
      setPreviewLoading(true)
      setPreviewError(null)
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }
      setPreviewBlob(null)

      let parsedMap = {}
      try {
        parsedMap = customFieldMapText.trim() ? JSON.parse(customFieldMapText) : {}
      } catch {}

      let parsedPdfPositions = {}
      try {
        parsedPdfPositions = pdfFieldPositionsText.trim() ? JSON.parse(pdfFieldPositionsText) : {}
      } catch {}

      const draft = {
        company_name: companyName,
        company_address: companyAddress,
        company_contacts: companyContacts,
        company_legal: companyLegal,
        footer_note: footerText,
        footer_text: footerText,
        accent_color: accentColor,
        show_logo: showLogo,
        page_format: pageFormat,
        invoice_prefix: invoicePrefix,
        invoice_number_format: invoiceNumberFormat,
        show_tva: showTva,
        show_remise: showRemise,
        show_payments: showPayments,
        show_installments: showInstallments,
        payment_terms: paymentTerms,
        iban_label: ibanLabel,
        iban_value: ibanValue,
        siret,
        ape_code: apeCode,
        convention_collective: conventionCollective,
        payroll_payment_method: payrollPaymentMethod,
        custom_field_map: parsedMap,
        custom_mode: useCustomTemplate ? 'overlay' : 'auto',
        pdf_field_positions: parsedPdfPositions,
      }

      const blob = await apiFetchBlob('/api/settings/invoice-format/preview', {
        method: 'POST',
        body: { draft, orderExternalId: previewOrderId || null }
      })

      const url = URL.createObjectURL(blob)
      setPreviewUrl(url)
      setPreviewBlob(blob)
    } catch (err) {
      setPreviewError(err?.message || 'Erreur preview PDF')
    } finally {
      setPreviewLoading(false)
    }
  }

  // -------- handleUploadLogo --------
  async function handleUploadLogo(e) {
    const file = e.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('logo', file)

    try {
      const resp = await fetch('/api/invoice-template/logo', {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      if (!resp.ok) {
        const txt = await resp.text()
        throw new Error(txt || 'upload failed')
      }

      const data = await resp.json()

      const newLogo = data.logo_path || data.template?.logo_path || null

      if (newLogo) {
        setLogoPath(newLogo)
        setShowLogo(true)
        setFlash('✅ Logo mis à jour')
      }
    } catch (err) {
      console.error('Upload logo error:', err)
      setFlash('❌ Erreur upload logo: ' + err.message)
    } finally {
      e.target.value = ''
    }
  }

  // -------- handleUploadCustomTemplate --------
  async function handleUploadCustomTemplate(e) {
    const file = e.target.files?.[0]
    if (!file) return

    // parse mapping
    let parsedMap = {}
    try {
      if (customFieldMapText.trim()) {
        parsedMap = JSON.parse(customFieldMapText)
      }
    } catch {
      setFlash('❌ custom_field_map invalide (JSON incorrect)')
      e.target.value = ''
      return
    }

    // parse pdf positions
    let parsedPdfPositions = {}
    try {
      if (pdfFieldPositionsText.trim()) {
        parsedPdfPositions = JSON.parse(pdfFieldPositionsText)
      }
    } catch {
      setFlash('❌ pdf_field_positions invalide (JSON incorrect)')
      e.target.value = ''
      return
    }

    const formData = new FormData()
    formData.append('template', file)
    formData.append('custom_field_map', JSON.stringify(parsedMap))
    formData.append('pdf_field_positions', JSON.stringify(parsedPdfPositions))

    try {
      const resp = await fetch('/api/invoice-template/custom-template', {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      if (!resp.ok) {
        const txt = await resp.text()
        throw new Error(txt || 'upload failed')
      }

      const data = await resp.json()

      setUseCustomTemplate(true)
      setCustomTemplateType(
        data.template?.custom_template_type ||
        data.template?.custom_template_type ||
        'docx'
      )

      setFlash('✅ Modèle personnalisé importé et activé')
      loadTemplate()
    } catch (err) {
      console.error('Upload template error:', err)
      setFlash('❌ Erreur upload modèle: ' + err.message)
    } finally {
      e.target.value = ''
    }
  }

  // -------- handleToggleCustomTemplate --------
  async function handleToggleCustomTemplate(enabled) {
    try {
      await apiFetch('/api/invoice-template/custom-template/toggle', {
        method: 'POST',
        body: { enabled }
      })
      
      setUseCustomTemplate(enabled)
      setFlash(
        enabled
          ? '✅ Modèle personnalisé ACTIVÉ'
          : 'ℹ️ Modèle personnalisé désactivé'
      )
      loadTemplate()
    } catch (err) {
      console.error('Toggle custom template error:', err)
      setFlash('❌ Erreur activation modèle: ' + err.message)
    }
  }

  // -------- handleDeleteCustomTemplate --------
  async function handleDeleteCustomTemplate() {
    if (!confirm('Êtes-vous sûr de vouloir désactiver le modèle personnalisé ?')) {
      return
    }

    try {
      await apiFetch('/api/invoice-template/custom-template', {
        method: 'DELETE'
      })
      
      setUseCustomTemplate(false)
      setCustomTemplateType(null)
      setFlash('✅ Modèle personnalisé désactivé')
      loadTemplate()
    } catch (err) {
      console.error('Delete custom template error:', err)
      setFlash('❌ Erreur suppression modèle: ' + err.message)
    }
  }

  // -------- saveFieldPositions (pour la modale PDF) --------
  async function saveFieldPositions(positions) {
    try {
      const response = await apiFetch('/api/settings/invoice-format', {
        method: 'PUT',
        body: {
          company_name: companyName,
          company_address: companyAddress,
          company_contacts: companyContacts,
          company_legal: companyLegal,
          footer_note: footerText,
          footer_text: footerText,
          accent_color: accentColor,
          show_logo: showLogo,
          page_format: pageFormat,
          invoice_prefix: invoicePrefix,
          invoice_number_format: invoiceNumberFormat,
          show_tva: showTva,
          show_remise: showRemise,
          show_payments: showPayments,
          show_installments: showInstallments,
          payment_terms: paymentTerms,
          iban_label: ibanLabel,
          iban_value: ibanValue,
        siret,
        ape_code: apeCode,
        convention_collective: conventionCollective,
        payroll_payment_method: payrollPaymentMethod,
          custom_field_map: JSON.parse(customFieldMapText || '{}'),
          pdf_field_positions: positions
        }
      })

      if (response) {
        setPdfFieldPositionsText(JSON.stringify(positions, null, 2))
        setFlash('✅ Positions PDF sauvegardées avec succès')
      } else {
        setFlash('❌ Erreur lors de la sauvegarde')
      }
    } catch (error) {
      console.error('Error saving positions:', error)
      setFlash('❌ Erreur de connexion')
    }
  }

  // -------- RENDER --------
  return (
    <>
      {/* INTRO + STATUS */}
      <section className="pilot-card">
        <div className="pilot-card-header border-b border-slate-200">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Format facture</h2>
              <p className="text-sm text-slate-500">
                Configure les informations visibles et le style de ta facture.
              </p>
            </div>
            <div className="pilot-badge">
              {useCustomTemplate ? 'Mode personnalisé' : 'Mode intégré'}
            </div>
          </div>
          <div className="pilot-divider" />
        </div>
        <div className="pilot-card-body grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 uppercase">Template</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {useCustomTemplate ? customTemplateType?.toUpperCase() || 'Personnalisé' : 'Intégré'}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {useCustomTemplate ? 'DOCX/PDF externe' : 'HTML + PDF'}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 uppercase">TVA</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">Défaut</div>
            <div className="text-xs text-slate-500 mt-1">Réglée dans Paramètres généraux</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 uppercase">Logo</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{showLogo ? 'Affiché' : 'Masqué'}</div>
            <div className="text-xs text-slate-500 mt-1">{logoPath ? 'Logo configuré' : 'Aucun logo'}</div>
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-6">
          {/* FORMULAIRE FORMAT FACTURE */}
          <section className="pilot-card">
            <div className="pilot-card-header border-b border-slate-200 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Modèle de facture intégré
                </h3>
                <p className="text-sm text-slate-500">
                  Option 1 : Configurez votre modèle avec les champs ci-dessous.
                </p>
              </div>
              {tplSaving && (
                <div className="text-xs text-slate-700 font-medium">
                  Sauvegarde...
                </div>
              )}
              <div className="pilot-divider" />
            </div>

            {tplLoading ? (
              <div className="p-8 text-center text-slate-500">
                Chargement du modèle...
              </div>
            ) : (
              <form
                className="pilot-card-body grid gap-6 md:grid-cols-2"
                onSubmit={handleSaveTemplate}
              >
            {/* Identité entreprise */}
            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500 uppercase">Identité entreprise</div>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Nom société / en-tête
              </label>
              <input
                className="pilot-input"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="FEDIS SARL"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Format de la facture
              </label>
              <select
                className="pilot-input"
                value={pageFormat}
                onChange={e => setPageFormat(e.target.value)}
              >
                <option value="A4">A4 (classique)</option>
                <option value="A5">A5 (demi-page)</option>
                <option value="ticket">Ticket (petit reçu)</option>
              </select>
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Adresse / Localisation
              </label>
              <textarea
                className="pilot-input min-h-[60px]"
                value={companyAddress}
                onChange={e => setCompanyAddress(e.target.value)}
                placeholder="Zone industrielle, Ouagadougou..."
              />
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Contacts visibles sur facture
              </label>
              <textarea
                className="pilot-input min-h-[60px]"
                value={companyContacts}
                onChange={e => setCompanyContacts(e.target.value)}
                placeholder="Tél: 70 00 00 00 / WhatsApp: ... / Email: ..."
              />
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Mentions légales / IFU / RCCM
              </label>
              <textarea
                className="pilot-input min-h-[60px]"
                value={companyLegal}
                onChange={e => setCompanyLegal(e.target.value)}
                placeholder="IFU: ... / RCCM: ..."
              />
            </div>

            {/* Style et footer */}
            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500 uppercase">Style et pied de page</div>
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Message bas de page
              </label>
              <textarea
                className="pilot-input min-h-[60px]"
                value={footerText}
                onChange={e => setFooterText(e.target.value)}
                placeholder="Merci pour votre confiance."
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Couleur d'accent (titres / bordures)
              </label>
              <input
                className="pilot-input"
                type="text"
                value={accentColor}
                onChange={e => setAccentColor(e.target.value)}
                placeholder="#1e293b"
              />
              <div className="text-xs text-slate-500 mt-1">
                Ex: #1e293b
              </div>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Logo société
              </label>

              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-slate-700 focus:ring-slate-300 border-border rounded"
                  checked={showLogo}
                  onChange={e => setShowLogo(e.target.checked)}
                />
                <span className="text-sm text-slate-900">
                  Afficher le logo sur la facture
                </span>
              </div>

              {logoPath ? (
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-xs text-slate-500">Logo actuel:</div>
                  <img
                    src={logoPath}
                    alt="logo société"
                    className="h-10 w-auto object-contain border border-slate-200 rounded bg-white p-1"
                  />
                </div>
              ) : (
                <div className="mb-3 text-xs text-slate-500 italic">
                  Aucun logo défini
                </div>
              )}

              <label className="text-xs font-medium text-slate-700 mb-1">
                Importer un nouveau logo (PNG/JPG, max 2Mo)
              </label>
              <input
                type="file"
                accept="image/*"
                className="text-sm"
                onChange={handleUploadLogo}
              />
            </div>

            {/* Numérotation */}
            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500 uppercase">Numérotation facture</div>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Préfixe
              </label>
              <input
                className="pilot-input"
                value={invoicePrefix}
                onChange={e => setInvoicePrefix(e.target.value)}
                placeholder="INV-"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Format (phase 2)
              </label>
              <input
                className="pilot-input"
                value={invoiceNumberFormat}
                onChange={e => setInvoiceNumberFormat(e.target.value)}
                placeholder="{PREFIX}{YYYY}{SEQ}"
              />
              <div className="text-xs text-slate-500 mt-1">
                Actuellement, le numéro affiché vient de la commande.
              </div>
            </div>

            {/* Affichage */}
            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500 uppercase">Affichage</div>
            </div>

            <div className="md:col-span-2 grid gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-3 text-sm text-slate-900">
                <input type="checkbox" checked={showTva} onChange={e => setShowTva(e.target.checked)} />
                Afficher TVA
              </label>
              <label className="flex items-center gap-3 text-sm text-slate-900">
                <input type="checkbox" checked={showRemise} onChange={e => setShowRemise(e.target.checked)} />
                Afficher remise
              </label>
              <label className="flex items-center gap-3 text-sm text-slate-900">
                <input type="checkbox" checked={showPayments} onChange={e => setShowPayments(e.target.checked)} />
                Afficher paiement
              </label>
              <label className="flex items-center gap-3 text-sm text-slate-900">
                <input type="checkbox" checked={showInstallments} onChange={e => setShowInstallments(e.target.checked)} />
                Afficher échéancier
              </label>
            </div>

            {/* Données paie */}
            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Données Bulletins de Salaire</div>
              <p className="text-xs text-slate-400 mt-1">Ces informations apparaissent sur les bulletins de paie générés pour vos employés.</p>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">N° RCCM / SIRET</label>
              <input
                className="pilot-input"
                value={siret}
                onChange={e => setSiret(e.target.value)}
                placeholder="Ex : BFOUA2013A3518 ou 123 456 789 00012"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">Code APE / Activité</label>
              <input
                className="pilot-input"
                value={apeCode}
                onChange={e => setApeCode(e.target.value)}
                placeholder="Ex : 6201Z"
              />
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-2">Convention collective applicable</label>
              <input
                className="pilot-input"
                value={conventionCollective}
                onChange={e => setConventionCollective(e.target.value)}
                placeholder="Ex : Convention collective du commerce – Burkina Faso"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">Mode de paiement des salaires</label>
              <select
                className="pilot-input"
                value={payrollPaymentMethod}
                onChange={e => setPayrollPaymentMethod(e.target.value)}
              >
                <option value="Virement">Virement bancaire</option>
                <option value="Espèces">Espèces</option>
                <option value="Chèque">Chèque</option>
                <option value="Mobile Money">Mobile Money</option>
                <option value="Orange Money">Orange Money</option>
                <option value="Wave">Wave</option>
              </select>
            </div>

            {/* Mentions & paiement */}
            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <div className="text-xs font-semibold text-slate-500 uppercase">Mentions & Paiement</div>
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Conditions de paiement
              </label>
              <textarea
                className="pilot-input min-h-[60px]"
                value={paymentTerms}
                onChange={e => setPaymentTerms(e.target.value)}
                placeholder="Paiement à 30 jours. Pénalités de retard: 1.5%/mois."
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Libellé IBAN / Mobile Money
              </label>
              <input
                className="pilot-input"
                value={ibanLabel}
                onChange={e => setIbanLabel(e.target.value)}
                placeholder="IBAN / Orange Money"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Valeur IBAN / Mobile Money
              </label>
              <input
                className="pilot-input"
                value={ibanValue}
                onChange={e => setIbanValue(e.target.value)}
                placeholder="SNXX XXXX XXXX XXXX"
              />
            </div>

            {/* Champs disponibles */}
            <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-semibold text-slate-700 uppercase">Champs disponibles (auto)</div>
              <div className="text-xs text-slate-600 mt-2">
                Ces champs sont injectés automatiquement depuis la commande. Utilise-les dans un modèle personnalisé.
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-700">
                <div>Facture: invoice_number, invoice_date</div>
                <div>Client: buyer_name, buyer_phone</div>
                <div>Totaux: subtotal, tva_pct, tva, total</div>
                <div>Remises: remise_pct, remise_amount, remise_total</div>
                <div>Paiement: payment_mode, payment_status, paid_total, remaining</div>
                <div>Échéances: installments_count, next_due_date, frequency</div>
                <div>Détails: payment_method, payment_provider, payment_reference</div>
                <div>Lignes: lines[].product_name, lines[].qty, lines[].unit_price, lines[].line_total</div>
              </div>
            </div>

            {/* Mapping champs personnalisé */}
            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Mapping champs personnalisé (JSON avancé)
              </label>
              <textarea
                className="pilot-input min-h-[140px] font-mono text-xs leading-relaxed"
                value={customFieldMapText}
                onChange={e => setCustomFieldMapText(e.target.value)}
                placeholder={`{\n  "InvoiceNumber": "invoice_number",\n  "Subtotal": "subtotal"\n}`}
              />
              <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                - Pour DOCX: les clés correspondent aux placeholders {"{{clé}}"} dans votre Word.<br/>
                - Pour PDF: les clés correspondent aux noms des champs du formulaire PDF (AcroForm).<br/>
                - Les valeurs indiquent quel champ interne FEDIS injecter
                (ex: "invoice_number", "total", "company_name"...).
              </p>
            </div>

            {/* Positions PDF */}
            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-2">
                Positions des champs PDF (JSON avancé)
              </label>
              <textarea
                className="pilot-input min-h-[140px] font-mono text-xs leading-relaxed"
                value={pdfFieldPositionsText}
                onChange={e => setPdfFieldPositionsText(e.target.value)}
                placeholder={`{\n  "buyer_name": { "x": 100, "y": 400, "fontSize": 11 },\n  "invoice_number": { "x": 100, "y": 380, "fontSize": 11 }\n}`}
              />
              <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                - Définit les coordonnées X,Y pour chaque champ sur le PDF.<br/>
                - Utilisez l'outil de calibration visuelle pour générer automatiquement ces positions.
              </p>
            </div>

            {/* bouton SAVE */}
            <div className="md:col-span-2 flex justify-end pt-4 border-t border-slate-200">
              <button
                type="submit"
                disabled={tplSaving}
                className={`pilot-btn ${
                  tplSaving
                    ? 'bg-slate-400 cursor-wait text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                Enregistrer le modèle
              </button>
            </div>
              </form>
            )}
          </section>

          {/* MODÈLE PERSONNALISÉ (DOCX/PDF) */}
          <section className="pilot-card">
            <div className="pilot-card-header border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Modèle personnalisé (DOCX/PDF)
              </h2>
              <p className="text-sm text-slate-500">
                Option 2 : Utilisez votre propre gabarit Word ou PDF.
              </p>
              <div className="pilot-divider" />
            </div>

            <div className="pilot-card-body grid gap-4">
              {/* Statut et activation */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-slate-900">
                    Statut :{' '}
                    <span
                      className={`font-medium ${
                        useCustomTemplate
                          ? 'text-green-600'
                          : 'text-slate-500'
                      }`}
                    >
                      {useCustomTemplate ? 'Activé' : 'Désactivé'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">
                    Type actuel : {customTemplateType || '—'}
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={useCustomTemplate}
                    onChange={e => handleToggleCustomTemplate(e.target.checked)}
                  />
                  <span>Activer le modèle personnalisé</span>
                </label>
              </div>

              {/* Upload modèle */}
              <div className="pt-2">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Importer / Remplacer le modèle (.docx ou .pdf)
                </label>
                <input
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                  onChange={handleUploadCustomTemplate}
                  className="text-sm"
                />
                <p className="text-xs text-slate-500 mt-2">
                  • DOCX avec placeholders: <code className="bg-surface-primary px-1 rounded">{'{{company_name}}'}</code>,{' '}
                  <code className="bg-surface-primary px-1 rounded">{'{{invoice_number}}'}</code>,{' '}
                  <code className="bg-surface-primary px-1 rounded">{'{{#lines}}...{{/lines}}'}</code>
                  <br />
                  • PDF avec champs de formulaire: InvoiceNumber, Total, etc.
                  <br />
                  • Le mapping JSON ci-dessus sera envoyé avec le modèle pour dire
                  quoi remplir.
                </p>
              </div>

              {/* Bouton calibration PDF */}
              {useCustomTemplate && customTemplateType?.includes('pdf') && (
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium text-slate-900 mb-2">🎯 Calibration PDF</h4>
                  <p className="text-xs text-slate-500 mb-3">
                    Définir visuellement les positions des champs sur votre PDF template
                  </p>
                  <button 
                    onClick={() => setCalibrationModalOpen(true)}
                    className="pilot-btn pilot-btn-primary"
                  >
                    📐 Ouvrir l'outil de calibration
                  </button>
                </div>
              )}

              {/* Bouton suppression */}
              {customTemplateType && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleDeleteCustomTemplate}
                    className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-2xl transition-colors"
                  >
                    Supprimer le modèle personnalisé
                  </button>
                  <p className="text-xs text-slate-500 mt-1">
                    Désactive le modèle personnalisé. (Le fichier peut rester côté serveur.)
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="lg:sticky lg:top-6 h-fit space-y-6">
          {/* PREVIEW FACTURE */}
          <section className="pilot-card">
            <div className="pilot-card-header border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Aperçu facture (exemple)
              </h2>
              <p className="text-sm text-slate-500">
                {useCustomTemplate 
                  ? 'Le modèle personnalisé sera utilisé pour les nouvelles factures.' 
                  : 'Ceci est un aperçu visuel HTML. Le PDF final utilisera ces infos.'}
              </p>
              <div className="pilot-divider" />
            </div>

            <div className="p-6 space-y-3">
              <div className="grid gap-2">
                <label className="text-xs font-semibold text-slate-600 uppercase">Commande exemple pour l’aperçu</label>
                <input
                  className="pilot-input"
                  placeholder="External ID (ex: CMD-2025-0001)"
                  value={previewOrderId}
                  onChange={e => setPreviewOrderId(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handlePreviewPdf}
                  className="pilot-btn pilot-btn-primary"
                  disabled={previewLoading}
                >
                  {previewLoading ? 'Génération...' : 'Aperçu PDF'}
                </button>
                {previewBlob && (
                  <button
                    type="button"
                    onClick={() => downloadBlob(previewBlob, 'invoice_preview.pdf')}
                    className="pilot-btn pilot-btn-outline"
                  >
                    Télécharger aperçu PDF
                  </button>
                )}
              </div>
              {previewError && (
                <div className="text-xs text-red-600">{previewError}</div>
              )}

              {previewUrl ? (
                <iframe
                  title="Aperçu PDF"
                  src={previewUrl}
                  className="w-full h-[520px] rounded-2xl border border-slate-200"
                />
              ) : useCustomTemplate ? (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 text-slate-700">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-900">
                        Modèle personnalisé activé
                      </h3>
                      <p className="text-sm text-slate-800 mt-1">
                        Les nouvelles factures utiliseront votre modèle{' '}
                        {customTemplateType
                          ? customTemplateType.toUpperCase()
                          : 'personnalisé'}
                        .
                        {customTemplateType === 'docx' &&
                          ' Assurez-vous que votre DOCX contient les placeholders appropriés.'}
                        {customTemplateType === 'pdf' &&
                          ' Assurez-vous que votre PDF contient des champs de formulaire nommés comme dans le mapping JSON.'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="border rounded-2xl p-4 text-xs leading-relaxed text-slate-900"
                  style={{
                    borderColor: accentColor || '#000',
                  }}
                >
                  {/* Header facture */}
                  <div
                    className="flex items-start gap-3 border-b pb-3"
                    style={{
                      borderColor: accentColor || '#000',
                      borderBottomWidth: 2
                    }}
                  >
                    {showLogo && (
                      <div className="flex-shrink-0">
                        {logoPath ? (
                          <img
                            src={logoPath}
                            alt="logo"
                            className="h-10 w-auto object-contain border rounded bg-surface-card p-1"
                          />
                        ) : (
                          <div className="h-10 w-10 border rounded bg-surface-primary flex items-center justify-center text-[9px] text-content-secondary">
                            LOGO ?
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex-1">
                      <div
                        className="font-bold text-[13px]"
                        style={{ color: accentColor || '#000' }}
                      >
                        {companyName || 'Nom Société'}
                      </div>
                      <div className="text-[10px] whitespace-pre-line">
                        {companyAddress || 'Adresse...'}
                      </div>
                      <div className="text-[10px] whitespace-pre-line mt-1">
                        {companyContacts || 'Tel / WhatsApp / Email'}
                      </div>
                      <div className="text-[10px] whitespace-pre-line mt-1 text-content-secondary">
                        {companyLegal || 'IFU: ...'}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 text-[10px]">
                    <div
                      className="font-semibold"
                      style={{ color: accentColor || '#000' }}
                    >
                      Facture: CMD-20251025-1234
                    </div>
                    <div>Date: 25/10/2025 16:30</div>
                    <div>Client: Jean Client · 70 00 00 00</div>
                  </div>

                  <table className="w-full mt-3 border-collapse text-[10px]">
                    <thead>
                      <tr
                        style={{
                          backgroundColor: '#f8fafc',
                          color: accentColor || '#000'
                        }}
                      >
                        <th className="text-left border px-1 py-1">Désignation</th>
                        <th className="text-right border px-1 py-1">Qté</th>
                        <th className="text-right border px-1 py-1">PU</th>
                        <th className="text-right border px-1 py-1">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border px-1 py-1">Filtre à eau Pro 20L</td>
                        <td className="border px-1 py-1 text-right">2</td>
                        <td className="border px-1 py-1 text-right">45 000</td>
                        <td className="border px-1 py-1 text-right">90 000</td>
                      </tr>
                      <tr>
                        <td className="border px-1 py-1">Cartouche rechange</td>
                        <td className="border px-1 py-1 text-right">1</td>
                        <td className="border px-1 py-1 text-right">5 000</td>
                        <td className="border px-1 py-1 text-right">5 000</td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="text-right text-[10px] mt-2 space-y-0.5">
                    <div>Total HT: 95 000 XOF</div>
                    <div>TVA: 5 000 XOF</div>
                    <div className="font-bold text-[12px]">
                      Total TTC: 100 000 XOF
                    </div>
                  </div>

                  <div className="mt-3 text-center text-[10px] text-content-secondary whitespace-pre-line">
                    {footerText || 'Merci pour votre confiance.'}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* MODAL CALIBRATION PDF */}
      <PdfCalibrationModal
        open={calibrationModalOpen}
        onClose={() => setCalibrationModalOpen(false)}
        template={{
          custom_template_path:
            useCustomTemplate && customTemplateType?.includes('pdf')
              ? 'path/to/your/pdf'
              : null,
          pdf_field_positions: JSON.parse(pdfFieldPositionsText || '{}')
        }}
        onSavePositions={saveFieldPositions}
      />
    </>
  )
}
